import type { Reader } from '@/api/client';
import { READER_COOPERATION_RAZOVO_ROW_BACKGROUND, STATUS_ROW_BACKGROUND } from '@/styles/projectColorTokens';

/** Совпадает с CHECK в БД и с `server/src/constants/readerCooperationStates.ts`. */
export const READER_COOPERATION_STATES = [
  'Регулярно',
  'Редко',
  'Разово',
  'Не сотрудничали',
  'Нет договора',
  'Автор',
  'Не указано',
  'В стоп-листе',
] as const;

export type ReaderCooperationStateValue = (typeof READER_COOPERATION_STATES)[number];

export const READER_COOPERATION_DEFAULT: ReaderCooperationStateValue = 'Не указано';

/** Font Awesome (free) — как в ячейке каталога чтецов и в селекторах. */
export const READER_COOPERATION_ICON_CLASS: Record<ReaderCooperationStateValue, string> = {
  Регулярно: 'fa-regular fa-circle-check',
  Редко: 'fa-solid fa-calendar-day',
  Разово: 'fa-solid fa-list-ol',
  'Не сотрудничали': 'fa-solid fa-person-circle-question',
  'Нет договора': 'fa-solid fa-file-circle-xmark',
  Автор: 'fa-solid fa-pen-nib',
  'Не указано': 'fa-regular fa-circle',
  'В стоп-листе': 'fa-solid fa-ban',
};

const VALID = new Set<string>(READER_COOPERATION_STATES);

/** Порядок в выпадающем списке (более активное сотрудничество сверху). */
export const READER_COOPERATION_MENU_ORDER: readonly ReaderCooperationStateValue[] = READER_COOPERATION_STATES;

const ROW_BACKGROUND: Record<ReaderCooperationStateValue, string> = {
  Регулярно: STATUS_ROW_BACKGROUND['Готов'],
  Редко: STATUS_ROW_BACKGROUND['Запись'],
  Разово: READER_COOPERATION_RAZOVO_ROW_BACKGROUND,
  'Не сотрудничали': '#ffffff',
  'Нет договора': '#ffffff',
  Автор: '#ffffff',
  'Не указано': '#ffffff',
  'В стоп-листе': '#EBEBEB',
};

export function normalizeReaderCooperationState(
  state: string | null | undefined
): ReaderCooperationStateValue {
  if (state && VALID.has(state)) return state as ReaderCooperationStateValue;
  return READER_COOPERATION_DEFAULT;
}

export function getReaderCooperationRowBackground(state: string | null | undefined): string {
  return ROW_BACKGROUND[normalizeReaderCooperationState(state)];
}

export function getReaderCooperationSortRank(state: string | null | undefined): number {
  const s = normalizeReaderCooperationState(state);
  switch (s) {
    case 'Регулярно':
      return 0;
    case 'Редко':
      return 1;
    case 'Разово':
      return 2;
    case 'Не сотрудничали':
      return 3;
    case 'Нет договора':
      return 4;
    case 'Автор':
      return 5;
    case 'Не указано':
      return 6;
    case 'В стоп-листе':
      return 7;
    default:
      return 6;
  }
}

export function sortReadersCatalogRows(readers: Reader[]): Reader[] {
  return [...readers].sort((a, b) => {
    const ra = getReaderCooperationSortRank(a.cooperation_state);
    const rb = getReaderCooperationSortRank(b.cooperation_state);
    if (ra !== rb) return ra - rb;
    const ln = (a.last_name ?? '').localeCompare(b.last_name ?? '', 'ru');
    if (ln !== 0) return ln;
    return (a.first_name ?? '').localeCompare(b.first_name ?? '', 'ru');
  });
}
